import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, Divider, 
  List, ListItem, ListItemText, ListItemButton,
  IconButton, Card, CardContent, Grid, Chip,
  Tooltip, Snackbar, Alert, CircularProgress,
  Badge, LinearProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate, useLocation } from 'react-router-dom';
import { neon } from '@neondatabase/serverless';
import AnalysisDialog from './AnalysisDialog';

// Get the database URL from environment variables or use direct string for development
const getDatabaseUrl = () => {
  return 'postgres://neondb_owner:npg_Ub9EiNceZ3ro@ep-divine-sun-a18caqng-pooler.ap-southeast-1.aws.neon.tech/neondb';
};

// Function to parse query parameters
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const History = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [databaseUrl] = useState(() => getDatabaseUrl());
  // Add state variables for AnalysisDialog
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [analysisAutoOpened, setAnalysisAutoOpened] = useState(false);
  const navigate = useNavigate();
  
  // Get query params
  const query = useQuery();
  const viewConversationId = query.get('view');

  // Initialize the database table on component mount
  useEffect(() => {
    initDatabase();
  }, []);

  // Watch for changes to selectedConversation to auto-open analysis dialog when viewing via URL
  useEffect(() => {
    // If a conversation is selected, it has analysis data, we're viewing via URL, and it hasn't been auto-opened yet
    if (selectedConversation && selectedConversation.analysis && viewConversationId && !analysisOpen && !analysisAutoOpened) {
      // Check if analysis dialog is not already open (redundant check now, but keep for clarity)
      if (!analysisOpen) { 
        console.log('Auto-loading analysis for conversation:', viewConversationId);
        // Load analysis data and open dialog
        fetchAnalysisData(selectedConversation.id)
          .then(result => {
            if (result) {
              // setAnalysisOpen(true); // This line opens the dialog
              // setAnalysisAutoOpened(true); // Mark as auto-opened
            }
          })
          .catch(error => {
            console.error('Error auto-loading analysis from selectedConversation change:', error);
          });
      }
    }
  }, [selectedConversation, viewConversationId, analysisOpen, analysisAutoOpened]);

  // Reset auto-open flag when viewConversationId changes
  useEffect(() => {
    setAnalysisAutoOpened(false);
  }, [viewConversationId]);

  // Load specific conversation if viewConversationId is provided
  useEffect(() => {
    if (viewConversationId) {
      // If conversations are already loaded, find the conversation
      if (conversations.length > 0) {
        const conversation = conversations.find(c => c.id === parseInt(viewConversationId));
        if (conversation) {
          setSelectedConversation(conversation);
          // Don't fetch analysis data here - the selectedConversation useEffect will handle it
        } else {
          // If not found in current state, try loading from database directly
          loadSpecificConversation(viewConversationId);
          // Don't fetch analysis data here - the selectedConversation useEffect will handle it
        }
      } else {
        // If conversations not yet loaded, load the specific one directly
        loadSpecificConversation(viewConversationId);
        // Don't fetch analysis data here - the selectedConversation useEffect will handle it
      }
    }
  }, [viewConversationId, conversations]);

  // Function to load a specific conversation by ID from the database
  const loadSpecificConversation = async (id) => {
    try {
      setIsLoading(true);
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // Get specific conversation by ID with analysis data if available
      const result = await sql`
        SELECT 
          c.*,
          ar.total_score,
          ar.completed_checkpoints,
          ar.incomplete_checkpoints,
          ar.completion_rate,
          ar.practice_time,
          (ar.id IS NOT NULL) as has_analysis
        FROM conversations c
        LEFT JOIN analysis_results ar ON c.id = ar.conversation_id
        WHERE c.id = ${parseInt(id)}
      `;
      
      if (result && result.length > 0) {
        // Format database result to component format
        const conversation = {
          id: result[0].id,
          scenarioId: result[0].scenario_id,
          scenarioTitle: result[0].scenario_title,
          timestamp: result[0].timestamp,
          messages: typeof result[0].messages === 'string' 
            ? JSON.parse(result[0].messages) 
            : result[0].messages,
          // Add analysis data if available
          analysis: result[0].has_analysis ? {
            totalScore: result[0].total_score,
            completedCheckpoints: result[0].completed_checkpoints,
            incompleteCheckpoints: result[0].incomplete_checkpoints,
            completionRate: Math.round(Number(result[0].completion_rate) || 0),
            practiceTime: Math.round(Number(result[0].practice_time) || 0)
          } : null
        };
        setSelectedConversation(conversation);
        return true; // Success
      } else {
        setSnackbarMessage(`找不到對話ID: ${id}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return false; // Failure
      }
    } catch (err) {
      console.error('Error loading conversation by ID:', err);
      setSnackbarMessage(`載入對話失敗: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return false; // Failure
    } finally {
      setIsLoading(false);
    }
  };

  // Function to initialize the database table
  const initDatabase = async () => {
    setIsLoading(true);
    
    try {
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // Test connection
      const testResult = await sql`SELECT 1 as test`;
      console.log('Connection test result:', testResult);
      setConnectionStatus('Connected to database');
      
      // Create the conversations table if it doesn't exist
      await sql`
        CREATE TABLE IF NOT EXISTS conversations (
          id BIGINT PRIMARY KEY,
          member_id BIGINT,
          scenario_id TEXT NOT NULL,
          scenario_title TEXT NOT NULL,
          timestamp TIMESTAMP WITH TIME ZONE NOT NULL,
          messages JSONB NOT NULL,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT (CURRENT_TIMESTAMP AT TIME ZONE 'Asia/Taipei')
        )
      `;

      // Add member_id column if it doesn't exist
      try {
        await sql`
          DO $$ 
          BEGIN
            IF NOT EXISTS (
              SELECT 1 
              FROM information_schema.columns 
              WHERE table_name = 'conversations' 
              AND column_name = 'member_id'
            ) THEN
              ALTER TABLE conversations ADD COLUMN member_id BIGINT;
            END IF;
          END $$;
        `;
        console.log('Successfully checked/added member_id column');
      } catch (err) {
        console.error('Error adding member_id column:', err);
      }
      
      // Load conversations from database
      await loadConversations();
      
    } catch (err) {
      console.error('Error initializing database:', err);
      setConnectionStatus(`Database connection failed: ${err.message}`);
      
      // Fall back to localStorage if database fails
      loadConversationsFromLocalStorage();
    } finally {
      setIsLoading(false);
    }
  };

  // Load conversations from database
  const loadConversations = async () => {
    try {
      setIsLoading(true);
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // Get member_id from localStorage using user object
      const userData = localStorage.getItem('user');
      let memberId = null;
      
      if (userData) {
        try {
          const user = JSON.parse(userData);
          memberId = user.id;
          console.log('Debug - Current user ID:', memberId); // Debug log
        } catch (error) {
          console.error('Error parsing user data:', error);
          setSnackbarMessage('請先登入以查看歷史對話');
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
          return;
        }
      } else {
        setSnackbarMessage('請先登入以查看歷史對話');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        return;
      }
      
      // Get conversations from database filtered by member_id
      // Join with analysis_results to get analysis data if available
      console.log('Debug - Fetching conversations for member_id:', memberId); // Debug log
      const dbConversations = await sql`
        SELECT 
          c.*,
          ar.total_score,
          ar.completed_checkpoints,
          ar.incomplete_checkpoints,
          ar.completion_rate,
          ar.practice_time,
          (ar.id IS NOT NULL) as has_analysis
        FROM conversations c
        LEFT JOIN analysis_results ar ON c.id = ar.conversation_id
        WHERE c.member_id = ${parseInt(memberId)}
        ORDER BY c.timestamp DESC
      `;
      
      if (dbConversations && dbConversations.length > 0) {
        // Convert db field names (snake_case) to component property names (camelCase)
        const formattedConversations = dbConversations.map(conv => ({
          id: conv.id,
          memberId: conv.member_id,
          scenarioId: conv.scenario_id,
          scenarioTitle: conv.scenario_title,
          timestamp: conv.timestamp,
          messages: typeof conv.messages === 'string' ? JSON.parse(conv.messages) : conv.messages,
          // Add analysis data if available
          analysis: conv.has_analysis ? {
            totalScore: conv.total_score,
            completedCheckpoints: conv.completed_checkpoints,
            incompleteCheckpoints: conv.incomplete_checkpoints,
            completionRate: Math.round(Number(conv.completion_rate) || 0),
            practiceTime: Math.round(Number(conv.practice_time) || 0)
          } : null
        }));
        setConversations(formattedConversations);
        console.log('Debug - Loaded conversations:', formattedConversations.length); // Debug log
      } else {
        // If no conversations in database, try loading from localStorage
        loadConversationsFromLocalStorage();
      }
    } catch (err) {
      console.error('Error loading conversations from database:', err);
      setSnackbarMessage(`載入對話失敗: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      // Fall back to localStorage if database fails
      loadConversationsFromLocalStorage();
    } finally {
      setIsLoading(false);
    }
  };

  // Load conversations from localStorage as fallback
  const loadConversationsFromLocalStorage = () => {
    const savedConversations = localStorage.getItem('chatHistory');
    if (savedConversations) {
      try {
        const parsedConversations = JSON.parse(savedConversations);
        // Filter conversations by current user's memberId
        const userData = localStorage.getItem('user');
        let memberId = null;
        
        if (userData) {
          try {
            const user = JSON.parse(userData);
            memberId = user.id;
          } catch (error) {
            console.error('Error parsing user data:', error);
          }
        }
        
        // Only show conversations for the current user
        const userConversations = memberId 
          ? parsedConversations.filter(conv => conv.memberId === memberId)
          : [];
        
        setConversations(userConversations);
        
        // If connected to database, sync localStorage conversations to database
        if (databaseUrl && connectionStatus === 'Connected to database') {
          syncLocalStorageToDatabase(userConversations);
        }
      } catch (error) {
        console.error('解析聊天歷史時出錯:', error);
        setConversations([]);
      }
    }
  };

  // Sync localStorage conversations to database
  const syncLocalStorageToDatabase = async (localConversations) => {
    try {
      if (!databaseUrl) return;
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // For each conversation in localStorage, insert into database if not exists
      for (const conversation of localConversations) {
        // Ensure we have valid data before inserting
        if (!conversation.id || !conversation.timestamp) {
          console.warn('Skipping invalid conversation object:', conversation);
          continue;
        }
        
        await sql`
          INSERT INTO conversations (id, scenario_id, scenario_title, timestamp, messages)
          VALUES (
            ${conversation.id},
            ${conversation.scenarioId || ''},
            ${conversation.scenarioTitle || 'Unknown Scenario'},
            ${new Date(conversation.timestamp)},
            ${JSON.stringify(conversation.messages)}
          )
          ON CONFLICT (id) DO NOTHING
        `;
      }
      
      console.log('Synced localStorage conversations to database');
    } catch (err) {
      console.error('Error syncing localStorage to database:', err);
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
  };

  const handleDeleteConversation = async (index, event) => {
    event.stopPropagation();
    
    try {
      const conversationToDelete = conversations[index];
      // Use the id property directly - it exists in both localStorage and database objects
      const conversationId = conversationToDelete.id;
      
      // Delete from database if connected
      if (databaseUrl && connectionStatus === 'Connected to database') {
        setIsLoading(true);
        const sql = neon(databaseUrl, { ssl: false });
        
        // Delete related analysis results first
        await sql`
          DELETE FROM analysis_results 
          WHERE conversation_id = ${conversationId}
        `;
        
        // Delete conversation 
        await sql`
          DELETE FROM conversations 
          WHERE id = ${conversationId}
        `;
      }
      
      // Update local state
      const updatedConversations = [...conversations];
      updatedConversations.splice(index, 1);
      setConversations(updatedConversations);
      
      // Update localStorage
      localStorage.setItem('chatHistory', JSON.stringify(updatedConversations));
      
      // Show success message
      setSnackbarMessage('對話已刪除');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Reset selected conversation if deleted
      if (selectedConversation && selectedConversation.id === conversationId) {
        setSelectedConversation(null);
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setSnackbarMessage(`刪除失敗: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToList = () => {
    setSelectedConversation(null);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('zh-TW', {
      timeZone: 'Asia/Taipei'
    });
  };

  const getScenarioName = (conversation) => {
    // Use the saved scenarioTitle if available (camelCase for localStorage objects)
    if (conversation.scenarioTitle) {
      return conversation.scenarioTitle;
    }
    
    // Check for snake_case format (from database)
    if (conversation.scenario_title) {
      return conversation.scenario_title;
    }
    
    // If no title is available, just return a default value
    return 'Unknown Scenario';
  };

  const handleCopyConversation = () => {
    if (!selectedConversation) return;
    
    const formattedMessages = selectedConversation.messages.map(message => {
      const sender = message.sender === 'user' ? 'You' : 'Customer';
      const time = formatDate(message.timestamp);
      return `${sender} (${time}): ${message.text}`;
    }).join('\n\n');
    
    const conversationInfo = `Scenario: ${getScenarioName(selectedConversation)}\nDate: ${formatDate(selectedConversation.timestamp)}\n\n`;
    
    const fullText = conversationInfo + formattedMessages;
    
    navigator.clipboard.writeText(fullText)
      .then(() => {
        setSnackbarMessage('對話已複製到剪貼簿');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      })
      .catch(err => {
        console.error('Failed to copy conversation:', err);
        setSnackbarMessage('Failed to copy conversation');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Function to handle clicking "View Analysis" button
  const handleViewAnalysis = async () => {
    if (!selectedConversation) return;
    
    try {
      // Fetch analysis data for the selected conversation
      const result = await fetchAnalysisData(selectedConversation.id);
      
      if (result) {
        // Open the analysis dialog
        setAnalysisOpen(true);
      }
    } catch (error) {
      console.error('Error viewing analysis:', error);
      setSnackbarMessage(`無法查看分析: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Function to format practice time
  const formatPracticeTime = (seconds) => {
    if (!seconds) return '00:00';
    return `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  // Function to fetch analysis data for a conversation
  const fetchAnalysisData = async (conversationId) => {
    try {
      setIsLoading(true);
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // Get analysis data for the conversation
      const result = await sql`
        SELECT * FROM analysis_results
        WHERE conversation_id = ${parseInt(conversationId)}
      `;
      
      if (result && result.length > 0) {
        // Get the analysis data
        let analysisJson = result[0].analysis_data;
        try {
          // Parse JSON if it's a string
          if (typeof analysisJson === 'string') {
            analysisJson = JSON.parse(analysisJson);
          }
          setAnalysisData(analysisJson);
          
          // Get nodes data if available
          let nodesData = result[0].nodes_data;
          if (nodesData) {
            // Parse JSON if it's a string
            if (typeof nodesData === 'string') {
              nodesData = JSON.parse(nodesData);
            }
            console.log('Loaded nodes data:', nodesData);
            setNodes(nodesData);
          } else {
            console.log('No nodes data available for this analysis');
            setNodes([]);
          }
          
          // Ensure practice_time is an integer
          const practiceTime = Math.round(Number(result[0].practice_time) || 0);
          
          return {
            analysisData: analysisJson,
            practiceTime: practiceTime,
            nodesData: nodesData || []
          };
        } catch (error) {
          console.error('Error parsing analysis data:', error);
          throw new Error('Invalid analysis data format');
        }
      } else {
        throw new Error('No analysis data found for this conversation');
      }
    } catch (err) {
      console.error('Error fetching analysis data:', err);
      setSnackbarMessage(`載入分析數據失敗: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        歷史對話
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {connectionStatus && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <Typography 
            color={connectionStatus.includes('failed') ? 'error' : 'success.main'}
            variant="body2"
          >
            {connectionStatus}
          </Typography>
        </Box>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : selectedConversation ? (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={handleBackToList}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {getScenarioName(selectedConversation)} - {formatDate(selectedConversation.timestamp)}
            </Typography>
            <Tooltip title="Copy conversation">
              <IconButton onClick={handleCopyConversation} aria-label="copy conversation">
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Analysis summary if available */}
          {selectedConversation.analysis && (
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                mb: 3, 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f7fa 100%)',
                borderLeft: '4px solid #3b82f6'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                gap: 2
              }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AssessmentIcon color="primary" />
                    評估報告
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">總得分</Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary.main">
                        {selectedConversation.analysis.totalScore}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">完成度</Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary.main">
                        {selectedConversation.analysis.completionRate}%
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">練習時間</Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary.main">
                        {formatPracticeTime(selectedConversation.analysis.practiceTime)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" textAlign="center">達成查核點</Typography>
                    <Box sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%', 
                      bgcolor: 'rgba(76, 175, 80, 0.15)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      color: 'success.main',
                      fontSize: '1.25rem'
                    }}>
                      {selectedConversation.analysis.completedCheckpoints}
                    </Box>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" textAlign="center">未達成查核點</Typography>
                    <Box sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%', 
                      bgcolor: 'rgba(244, 67, 54, 0.15)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      color: 'error.main',
                      fontSize: '1.25rem'
                    }}>
                      {selectedConversation.analysis.incompleteCheckpoints}
                    </Box>
                  </Box>
                  
                  <Box sx={{ ml: 2 }}>
                    <Tooltip title="View full analysis report">
                      <IconButton 
                        color="primary" 
                        onClick={handleViewAnalysis}
                        aria-label="view analysis"
                        sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          }
                        }}
                      >
                        <AssessmentIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}
          
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Chip 
                label={`Scenario: ${getScenarioName(selectedConversation)}`} 
                sx={{ mr: 1, mb: 1 }} 
              />
              <Chip 
                label={`Date: ${formatDate(selectedConversation.timestamp)}`} 
                sx={{ mr: 1, mb: 1 }} 
              />
              <Chip 
                label={`Messages: ${selectedConversation.messages.length}`} 
                sx={{ mr: 1, mb: 1 }} 
              />
            </Box>
            
            <Box sx={{ maxHeight: '60vh', overflow: 'auto', p: 2 }}>
              {selectedConversation.messages.map((message, index) => (
                <Box 
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                    mb: 2
                  }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      backgroundColor: message.sender === 'user' ? '#e3f2fd' : '#f5f5f5',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="subtitle2" color="textSecondary">
                      {message.sender === 'user' ? 'You' : 'Customer'} - {formatDate(message.timestamp)}
                    </Typography>
                    <Typography variant="body1">
                      {message.text}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {conversations.length > 0 ? (
            conversations.map((conversation, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 6 },
                    position: 'relative',
                    ...(conversation.analysis && {
                      borderLeft: '4px solid #3b82f6'
                    })
                  }}
                  onClick={() => handleSelectConversation(conversation)}
                >
                  {/* Position delete button absolutely at top-right corner */}
                  <IconButton 
                    size="small" 
                    onClick={(e) => handleDeleteConversation(index, e)}
                    aria-label="delete conversation"
                    sx={{ 
                      position: 'absolute', 
                      top: 10, 
                      right: 10, 
                      zIndex: 2,
                      bgcolor: 'rgba(255,255,255,0.7)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  
                  {/* Move score chip to left side of card */}
                  {conversation.analysis && (
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: 10, 
                        left: 10,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        zIndex: 1
                      }}
                    >
                      <Chip
                        size="small"
                        label={`Score: ${conversation.analysis.totalScore}`}
                        color="primary"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  )}
                  
                  <CardContent sx={{ pt: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Typography variant="h6" noWrap sx={{ width: '100%' }}>
                        {getScenarioName(conversation)}
                      </Typography>
                    </Box>
                    <Typography color="textSecondary">
                      {formatDate(conversation.timestamp)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {conversation.messages.length} messages
                    </Typography>
                    
                    {conversation.analysis && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            Completion: {conversation.analysis.completionRate}%
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                            <Typography variant="caption">
                              {conversation.analysis.completedCheckpoints} / {conversation.analysis.completedCheckpoints + conversation.analysis.incompleteCheckpoints}
                            </Typography>
                          </Box>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={conversation.analysis.completionRate} 
                          sx={{ height: 4, borderRadius: 2 }}
                        />
                      </Box>
                    )}
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mt: 1, 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {conversation.messages[0]?.text || 'No messages'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                  No conversation history found
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Start a new conversation to see it here
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* Add AnalysisDialog component */}
      <AnalysisDialog
        open={analysisOpen}
        onClose={() => setAnalysisOpen(false)}
        data={analysisData}
        nodes={nodes}
        practice_time={selectedConversation?.analysis?.practiceTime ? Math.round(Number(selectedConversation.analysis.practiceTime)) : 0}
        conversationId={selectedConversation?.id}
      />
    </Container>
  );
};

export default History; 